import React, { Component } from 'react';
import { isEqual, sum, get } from 'lodash';
import PropTypes from 'prop-types';
import memoize from 'memoize-one';
import helpers from 'helpers';
import ExportableTable from 'containers/tables/ExportableTable';
import CellDiv from 'components/molecules/tables/components/CellDiv';
import ContentTile from 'components/atoms/layout/ContentTile';
import ContentHeader from 'components/atoms/layout/ContentHeader';
import Pagination, { RowsPerPageDefault } from 'components/molecules/tables/components/Pagination';
import FilterBar from 'components/atoms/tables/FilterBar';
import FilterLabel from 'components/atoms/tables/FilterLabel';
import ToggleSwitch from 'components/atoms/form/ToggleSwitch';
import contentPadding from 'helpers/contentPadding';
import { generateFooterData } from 'helpers/tables';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { convertToHeaderDisplayValue } from 'helpers/strings';
import { ATTRIBUTION_ACTIVITY_CHANNELS } from 'constants/channels';

const cellStyle = {
    backgroundColor: 'rgba(200, 200, 200, 0.15)'
};

class ActivitiesPivotTable extends Component {
    constructor(props) {
        super(props);
        this.prepareActivityTableData = memoize(ActivitiesPivotTable.prepareActivityTableData);
        this.state = {
            showAttribution: false
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    }

    static prepareActivityTableData(activityKeys, activityData, filterField) {
        const processedData = [];
        activityKeys.forEach((key) => {
            const channelsData = get(activityData, [key, 'channelBreakdown'], {});
            Object.keys(channelsData).forEach((channel) => {
                if (get(channelsData, [channel, 'statistics', filterField], 0) > 0) {
                    processedData.push({
                        activityName: key,
                        channel,
                        ...channelsData[channel].statistics
                    });
                }
            });
        });

        const uniqueActions = new Set(processedData.map(c => c.activityName)).size;
        const uniqueChannels = [...new Set(processedData.map(c => c.channel))];

        return { processedData, uniqueActions, uniqueChannels };
    }

    static formatAttributableChannelCell(row) {
        const subRow = get(row, 'level', 0) > 0;
        const pivotRow = get(row, 'subRows', []).length > 1; // This is > 1 instead of > 0 because subRows include a copy of the row in subRows.
        const collapsedPivotRow = !get(row, 'isExpanded');
        const numOfAttributableChannels = get(row, 'subRows', []).filter(item => ATTRIBUTION_ACTIVITY_CHANNELS.includes(item.channel)).length;
        const channel = subRow ? get(row, 'row.channel', undefined) : get(row, 'subRows[0].channel', undefined);
        const attributableChannel = ATTRIBUTION_ACTIVITY_CHANNELS.includes(channel);
        const pivotRowValue = collapsedPivotRow ? `${numOfAttributableChannels} ${numOfAttributableChannels > 1 ? 'Channels' : 'Channel'}` : '';
        return (
            <CellDiv
                disabled={!attributableChannel}
                value={pivotRow ? pivotRowValue : row.value}
                type={pivotRow ? 'string' : 'number'} />
        );
    }

    render() {
        const {
            title,
            exportName,
            data,
            objectives,
            showTotalActivities,
            entityObjectiveLabelCode,
            filterField = 'ecpa_activities',
            caption
        } = this.props;
        const { showAttribution } = this.state;
        const { processedData, uniqueActions, uniqueChannels } =
        this.prepareActivityTableData(data.keys, data.data, filterField);

        const showAttributionToggle = uniqueChannels.some(channel => ATTRIBUTION_ACTIVITY_CHANNELS.includes(channel));
        const includesECPA = objectives.includes('ecpa');
        let defaultSort = { id: 'activityName', desc: true };
        const footerData = generateFooterData({ data: processedData, filteredKeys: ['activityName'] });
        const {
            singularTerm,
            pluralTerm,
            primaryActivityFieldTerm,
        } = getActivitiesSettings(objectives[0], entityObjectiveLabelCode);

        const handleShowAttribution = () => {
            this.setState({
                showAttribution: !this.state.showAttribution
            });
        };

        const defaultColumns = [{
            Header: singularTerm.toUpperCase(),
            accessor: 'activityName',
            PivotValue: ({ value }) => <span>{value}</span>,
            Footer: (
                <CellDiv value='Total' strong />
            )
        },
        {
            Header: 'CHANNEL',
            accessor: 'channel',
            type: 'string',
            Aggregated: (row) => {
                const count = row.subRows.length;
                let value = `${count} Channels`;
                if (count === 1) {
                    value = helpers.convertToDisplayValue(row.subRows[0].channel);
                } else if (row.isExpanded) {
                    value = '';
                }
                return (
                    <CellDiv
                        strong={count !== 1}
                        value={value}
                        style={{ textAlign: 'left' }}
                    />
                );
            },
            Cell: row => <CellDiv value={helpers.convertToDisplayValue(row.value)} style={{ textAlign: 'left' }} />,
            Footer: (
                <CellDiv value=' ' strong />
            )
        }];

        let columns = defaultColumns;

        if (showAttribution) {
            columns = [{
                Header: ' ',
                columns: defaultColumns
            }];
        }


        if (showTotalActivities) {
            defaultSort = { id: 'total_activities', desc: true };
            const showTotalActivityColumns = [
                {
                    Header: showAttribution ? 'TOTAL' : 'TOTAL ACTIVITIES',
                    accessor: 'total_activities',
                    type: 'number',
                    aggregate: values => sum(values),
                    style: showAttribution ? cellStyle : null,
                    Cell: row => <CellDiv value={row.value} type="number" />,
                    Footer: (
                        <CellDiv value={footerData.total_activities} type="number" strong />
                    )
                }
            ];

            if (showAttribution) {
                showTotalActivityColumns.unshift(
                    {
                        Header: 'POST-IMPRESSION',
                        accessor: 'total_post_impression_activity_count',
                        type: 'number',
                        aggregate: values => sum(values),
                        style: showAttribution ? cellStyle : null,
                        Cell: row => ActivitiesPivotTable.formatAttributableChannelCell(row),
                        Footer: (
                            <CellDiv value={footerData.total_post_impression_activity_count} type="number" strong />
                        )
                    },
                    {
                        Header: 'POST-CLICK',
                        accessor: 'total_post_click_activity_count',
                        type: 'number',
                        aggregate: values => sum(values),
                        style: includesECPA ? cellStyle : null,
                        Cell: row => ActivitiesPivotTable.formatAttributableChannelCell(row),
                        Footer: (
                            <CellDiv value={footerData.total_post_click_activity_count} type="number" strong />
                        )
                    }
                );
            }
            // if showing attribution, we have a header row and 3 columns,
            // otherwise it is just the ECPA Activities column
            columns.push(showAttribution ? {
                Header: `TOTAL ${pluralTerm.toUpperCase()}`,
                columns: showTotalActivityColumns
            } : showTotalActivityColumns[0]);
        }

        if (includesECPA) {
            defaultSort = { id: 'ecpa_activities', desc: true };
            const includesECPAColumns = [
                {
                    Header: showAttribution ? 'TOTAL' : convertToHeaderDisplayValue(primaryActivityFieldTerm),
                    accessor: 'ecpa_activities',
                    type: 'number',
                    aggregate: values => sum(values),
                    Cell: row => <CellDiv value={row.value} type="number" />,
                    Footer: (
                        <CellDiv value={footerData.ecpa_activities} type="number" strong />
                    )
                }
            ];
            if (showAttribution) {
                includesECPAColumns.unshift(
                    {
                        Header: 'POST-IMPRESSION',
                        accessor: 'ecpa_post_impression_activity_count',
                        type: 'number',
                        aggregate: values => sum(values),
                        Cell: row => ActivitiesPivotTable.formatAttributableChannelCell(row),
                        Footer: (
                            <CellDiv value={footerData.ecpa_post_impression_activity_count} type="number" strong />
                        )
                    },
                    {
                        Header: 'POST-CLICK',
                        accessor: 'ecpa_post_click_activity_count',
                        type: 'number',
                        aggregate: values => sum(values),
                        Cell: row => ActivitiesPivotTable.formatAttributableChannelCell(row),
                        Footer: (
                            <CellDiv value={footerData.ecpa_post_click_activity_count} type="number" strong />
                        )
                    }
                );
            }
            // if showing attribution, we have a header row and 3 columns,
            // otherwise it is just the Total Activities column
            columns.push(showAttribution ? {
                Header: convertToHeaderDisplayValue(primaryActivityFieldTerm),
                columns: includesECPAColumns
            } : includesECPAColumns[0]);
        }

        return (
            <ContentTile padding={contentPadding('table')}>
                <ContentHeader tableData={processedData} exportName={exportName} type={'table'}>{title}</ContentHeader>
                { showAttributionToggle && <FilterBar>
                    <ToggleSwitch onClick={handleShowAttribution} value={!!showAttribution} scale="0.5" />
                    <FilterLabel>Show Impression/Click Attribution</FilterLabel>
                </FilterBar>
                }
                <ExportableTable
                    key={1}
                    title={title}
                    headerDepth={2}
                    exportName={exportName}
                    data={processedData}
                    columns={columns}
                    pivotBy={['activityName']}
                    getTrProps={(state, rowInfo) => {
                        const hasSubRows = !!get(rowInfo, 'subRows', []).length > 0;
                        if (hasSubRows) {
                            return { 'row-count': rowInfo.subRows.length - 1 };
                        }
                        return {};
                    }}
                    getTfootProps={() => ({ style: { backgroundColor: '#E2E2E2', fontWeight: 'bold', boxShadow: 'none' } })}
                    getTheadThProps={() => ({ style: { outline: 0 } })}
                    defaultPageSize={RowsPerPageDefault}
                    exportRows={processedData.length}
                    showPagination={uniqueActions > RowsPerPageDefault}
                    PaginationComponent={Pagination}
                    defaultSorted={[defaultSort]}
                    minRows={uniqueActions < RowsPerPageDefault ? uniqueActions : RowsPerPageDefault}
                    className="-highlight"
                />
                {caption && <p style={{ padding: '10px 0px 0px 8px' }}>{caption}</p>}
            </ContentTile>
        );
    }
}

ActivitiesPivotTable.propTypes = {
    data: PropTypes.shape({
        keys: PropTypes.arrayOf(PropTypes.string).isRequired,
        data: PropTypes.object.isRequired
    }),
    title: PropTypes.string,
    filterField: PropTypes.string,
    objectives: PropTypes.arrayOf(PropTypes.string),
    showTotalActivities: PropTypes.bool,
    entityObjectiveLabelCode: PropTypes.string,
    caption: PropTypes.string
};

export default ActivitiesPivotTable;



// WEBPACK FOOTER //
// ./src/components/molecules/tables/ActivitiesPivotTable.js